import { DEFAULT_TRUNCATION_MODE, DISPLAY_NAME } from './constants.js';
import { SankeyConfigProvider } from './providers/SankeyConfigProvider.js';
import { SankeyDataProvider } from './providers/SankeyDataProvider.js';
import { SankeyChartLayout } from './SankeyChart.js';
import { Nodes } from './slots/Nodes.js';
import type { SankeyChartProps } from './types/sankey-props.js';
import { iterateSlots } from './utils/iterate-slots.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';

/**
 * @internal
 */
export const SankeyChartRoot = (props: SankeyChartProps) => {
  const {
    data,
    sourceAccessor,
    targetAccessor,
    valueAccessor,
    nodes,
    nodeIdAccessor,
    nodeLabelAccessor,
    columnAccessor,
    width,
    height,
    loading = false,
    truncationMode = DEFAULT_TRUNCATION_MODE,
    colorPalette = 'categorical',
    children,
  } = props;

  const { nodes: nodesConfig } = iterateSlots(children);

  return (
    <SankeyConfigProvider
      nodesConfig={nodesConfig}
      truncationMode={truncationMode}
      colorPalette={colorPalette}
    >
      <SankeyDataProvider
        data={data}
        sourceAccessor={sourceAccessor}
        targetAccessor={targetAccessor}
        valueAccessor={valueAccessor}
        nodes={nodes}
        nodeIdAccessor={nodeIdAccessor}
        nodeLabelAccessor={nodeLabelAccessor}
        columnAccessor={columnAccessor}
      >
        <SankeyChartLayout
          chartWidth={width}
          chartHeight={height}
          showLoader={loading}
        >
          {children}
        </SankeyChartLayout>
      </SankeyDataProvider>
    </SankeyConfigProvider>
  );
};

(
  SankeyChartRoot as typeof SankeyChartRoot & { displayName: string }
).displayName = DISPLAY_NAME;

/**
 * The `SankeyChart` visualizes directed flows between nodes across stages.
 * @internal
 */
export const SankeyChart = Object.assign(SankeyChartRoot, {
  Nodes,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
});

(SankeyChart as typeof SankeyChart & { displayName: string }).displayName =
  'SankeyChart';
